import React from 'react';
import { useSelector } from 'react-redux';
import FlowmaPowered from '../../Components/powered-by-flowma-component';
import { getFooterData } from '../../Public/footer-data';
import SystemDataService from '../../Services/SystemDataService';
import NavLink from '../NavLink/navlink-component';

function Footer() {
    const systemData = useSelector((s) => s.systemData || {});
    const { dir, language, businessInfo = {}, links = [] } = systemData;

    const txt = getFooterData(language);

    const whatsappLink = SystemDataService.byLabel(links, 'WhatsApp');
    const instagramLink = SystemDataService.byLabel(links, 'Instagram');
    const facebookLink = SystemDataService.byLabel(links, 'Facebook');
    const logoWhite = SystemDataService.byLabel(links, 'Logo White');
    const mapsLink = SystemDataService.byLabel(links, 'Google Maps');
    const phoneLink = SystemDataService.byLabel(links, 'Phone');
    const emailLink = SystemDataService.byLabel(links, 'Email');

    const { address, hours, name } = businessInfo;

    return (
        <div dir={dir} className='flex w-full bottom-0'>
            <div className='block w-full z-10'>
                <footer className="bg-black text-white transition-colors duration-300">

                    <div className="flex p-4 sm:p-8 justify-center flex-col items-center">

                        {/* Main Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl w-full text-center lg:text-start items-start">

                            {/* Column: Contact */}
                            <div className="flex flex-col gap-2">
                                <NavLink href="/contact">
                                    <h2 className="text-lg font-bold mb-2 underline decoration-white/40 underline-offset-4 uppercase tracking-wider">
                                        {txt.contactHeading}
                                    </h2>
                                </NavLink>
                                <NavLink href={'/about'} className="text-sm opacity-80 hover:opacity-100">{txt.aboutHeading}</NavLink>
                                {phoneLink && (
                                    <NavLink href={phoneLink.url} className="text-sm opacity-80 hover:opacity-100">{phoneLink.description}</NavLink>
                                )}
                                {emailLink && (
                                    <NavLink href={emailLink.url} className="text-sm opacity-80 hover:opacity-100 break-all">{emailLink.description}</NavLink>
                                )}
                            </div>

                            {/* Column: Services */}
                            <div className="flex flex-col gap-2">
                                <NavLink href="/services">
                                    <h2 className="text-lg font-bold mb-2 underline decoration-white/40 underline-offset-4 uppercase tracking-wider">
                                        {txt.ServicesHeading}
                                    </h2>
                                </NavLink>
                                {(txt.services || []).map((s) => (
                                    <NavLink key={s.slug} href={`/services/${s.slug}`} className="text-sm opacity-80 hover:opacity-100">
                                        {s.label}
                                    </NavLink>
                                ))}
                            </div>

                            {/* Column: Hours */}
                            <div className="flex flex-col items-center gap-2">
                                <h2 className="text-lg font-bold mb-2 underline decoration-white/40 underline-offset-4 uppercase tracking-wider">{txt.hoursHeading}</h2>
                                <ul className="text-sm space-y-2 opacity-80">
                                   {hours && Object.entries(hours).map(([day, time]) => (
                                        <li key={day} className="grid grid-cols-[5rem_1fr] gap-10 sm:gap-4">
                                            <span className="capitalize text-white/60 font-medium">{day}</span>
                                            <span className={`font-semibold ${time === 'Closed' ? 'opacity-40' : ''}`}>{time}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Column: Address */}
                            <div className="flex flex-col gap-2">
                                <h2 className="text-lg font-bold mb-2 underline decoration-white/40 underline-offset-4 uppercase tracking-wider">{txt.addressHeading}</h2>
                                {address && (
                                    <NavLink href={mapsLink?.url || '#'} target="_blank" className="text-sm opacity-80 hover:opacity-100 leading-relaxed">
                                        {address}
                                    </NavLink>
                                )}
                            </div>
                        </div>

                        {/* --- Social & Logo Bar --- */}
                        <div className="flex flex-col sm:flex-row justify-between items-center py-6 mt-10 border-t border-white/5 w-full max-w-7xl gap-6">
                            {logoWhite?.image && <img src={logoWhite.image} alt="Logo" className="h-8 w-auto opacity-90" />}

                            <div className="flex gap-6 items-center">
                                {instagramLink?.url && (
                                    <NavLink href={instagramLink.url} target="_blank" showUnderline={false} className="hover:opacity-70 transition-opacity invert">
                                        <img src={instagramLink.image} alt="Instagram" className="w-8 h-8" />
                                    </NavLink>
                                )}
                                {facebookLink?.url && (
                                    <NavLink href={facebookLink.url} target="_blank" showUnderline={false} className="hover:opacity-70 transition-opacity invert">
                                        <img src={facebookLink.image} alt="Facebook" className="w-8 h-8" />
                                    </NavLink>
                                )}
                                {whatsappLink?.url && (
                                    <NavLink href={whatsappLink.url} target="_blank" showUnderline={false} className="hover:opacity-70 transition-opacity">
                                        <img src={whatsappLink.image} alt="WhatsApp" className="w-8 h-8" />
                                    </NavLink>
                                )}
                            </div>
                        </div>

                        {/* --- Bottom Bar --- */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full max-w-7xl pt-4 border-t border-white/5 gap-4">
                            <span className="text-[11px] opacity-50 text-center sm:text-start uppercase">
                                © {new Date().getFullYear()} {name || txt.copyrightText}
                            </span>
                            <div className="flex flex-wrap gap-4 text-[11px] justify-center opacity-60 font-medium">
                                <NavLink href="/privacy">{txt.privacyText}</NavLink>
                                <NavLink href="/terms">{txt.termsText}</NavLink>
                            </div>
                        </div>
                    </div>

                    <div className="pb-4 opacity-70 scale-90">
                        <FlowmaPowered language={language} seo={false} />
                    </div>
                </footer>
            </div>
        </div>
    );
}

export default Footer;
